import Cookies from 'js-cookie';
import router from '../router';
import { inject, reactive, readonly, toRefs } from 'vue';
import useFlashMessage from './useFlashMessage';
import callApi from '../api/ApiClient';

const AUTH_STORE_SYMBOL = Symbol('auth store symbol');
const APP_SESSION_KEY = 'app_session';

export function isAuthenticated() {
  return !!Cookies.get(APP_SESSION_KEY);
}

export function storeCookie() {
  Cookies.set(APP_SESSION_KEY, true, { expires: 86400, sameSite: 'lax' });
}

export function removeCookie() {
  Cookies.remove(APP_SESSION_KEY);
}

const state = reactive({
  user: null,
  isLoading: false,
  error: '',
});

/**
 * @param {User|null} user
 */
function setUser(user) {
  state.user = user;
}

/**
 * ログイン処理
 *
 * @param email
 * @param password
 * @returns {Promise<void>}
 */
async function login(email, password) {
  const { showFlashMessage } = useFlashMessage();

  const { error } = await callApi({
    method: 'POST',
    url: '/api/login',
    data: { email, password },
  });

  if (error) {
    switch (error.status) {
      case 422:
        showFlashMessage('danger', 'メールアドレスまたはパスワードが正しくありません。');
        return;
      default:
        showFlashMessage('danger', 'ログインに失敗しました。');
        return;
    }
  }

  storeCookie();

  await router.push('/');
}

/**
 * ログアウト処理
 *
 * @returns {Promise<void>}
 */
async function logout() {
  const { showFlashMessage } = useFlashMessage();

  const { error } = await callApi({ method: 'POST', url: '/api/logout' });
  if (error) {
    state.error = error;
    return;
  }

  state.user = null;

  removeCookie();

  showFlashMessage('success', 'ログアウトしました。');

  await router.push('/login');
}

export function createAuthStore() {
  const authStore = {
    ...toRefs(readonly(state)),
    setUser,
    login,
    logout,
  };

  // installを持たせてvueのプラグイン化にし、authStoreをapp全体にprovideする
  return {
    authStore,
    install: (app) => {
      app.provide(AUTH_STORE_SYMBOL, authStore);
    },
  };
}

/**
 * ログイン中のユーザーやログイン、ログアウト用の関数などをまとめたオブジェクトを返す
 *
 * @returns {{user, login, logout}}
 */
export function useAuth() {
  return inject(AUTH_STORE_SYMBOL);
}
